// https://help.fullstory.com/hc/en-us/articles/360020623234-Client-API-Requirements#Custom%20Event%20Name%20Requirements
export abstract class AnalyticsCustomEvent {
  constructor(
    readonly name: string,
    readonly properties: { [key: string]: unknown }
  ) {}
}

class SimpleCustomEvent extends AnalyticsCustomEvent {
  constructor(name: string) {
    super(name, {});
  }
}

export const SimpleEvent = Object.freeze({
  REPEATING_DECIMAL_CLICK: new SimpleCustomEvent(
    'Clicked Repeating Decimal Toolbar Button'
  ),
});

export class RuntimeExceptionEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Runtime Exception';
  constructor(errorMessage: string) {
    super(RuntimeExceptionEvent.EVENT_NAME, {
      'error_message_str': errorMessage,
    });
  }
}

// Emitted when a student-facing question fails to render — typically because
// `parser.parseQuestion` threw on malformed omaha code. Filter Cloud Logging
// on event.name = "Question Render Error" to see every question that's
// currently broken in production. The version key is included so support can
// jump straight to the failing question in the authoring tool.
export class QuestionRenderErrorEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Question Render Error';
  constructor(opts: {
    questionVersionKey: string;
    errorMessage: string;
    errorName: string;
  }) {
    super(QuestionRenderErrorEvent.EVENT_NAME, {
      'question_version_key_str': opts.questionVersionKey,
      'error_message_str': opts.errorMessage,
      'error_name_str': opts.errorName,
    });
  }
}

export class ImportFromPreviousCourseEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Import From Previous Course';
  constructor(courseRef: string) {
    super(ImportFromPreviousCourseEvent.EVENT_NAME, {
      'source_course_str': courseRef,
    });
  }
}

// Emitted when an author deletes a book from the booklist. Book deletion is a
// direct client-side Firestore transaction with no server request behind it,
// so nothing server-side sees it happen and there is no audit-log entry to
// find later. Filter Cloud Logging on event.name = "Book Deleted" to see who
// removed a book and when.
//
// hard_deleted_bool distinguishes the two branches of
// BooklistState.deleteBook: a soft delete leaves the document in place with
// is_deleted = true, while an admin deleting an empty book removes it
// outright, which is the case no state on the document itself can record.
export class BookDeletedEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Book Deleted';
  constructor(opts: {
    bookRef: string;
    bookTitle: string;
    hardDeleted: boolean;
  }) {
    super(BookDeletedEvent.EVENT_NAME, {
      'book_ref_str': opts.bookRef,
      'book_title_str': opts.bookTitle,
      'hard_deleted_bool': opts.hardDeleted,
    });
  }
}

export type FirebasePhase = 'auth' | 'first_snapshot' | 'read';

// Emitted when the browser cannot reach Firebase Auth or Firestore: a sign-in
// rejects or stalls, a listener gets no server snapshot, or a read fails with
// "client is offline". Those calls bypass our servers, so a network blocking
// Google's hosts is otherwise invisible to us. error_code_str is the Firebase
// network error code (auth/network-request-failed, auth/timeout, unavailable,
// deadline-exceeded) or "timeout"; errors Firebase answered are not reported.
// For phase "read", latency_ms_int is time since the page started loading.
export class FirebaseUnreachableEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Firebase Unreachable';
  constructor(opts: {
    phase: FirebasePhase;
    surface: string;
    errorCode: string;
    latencyMs: number;
  }) {
    super(FirebaseUnreachableEvent.EVENT_NAME, {
      'phase_str': opts.phase,
      'surface_str': opts.surface,
      'error_code_str': opts.errorCode,
      'latency_ms_int': Math.round(opts.latencyMs),
    });
  }
}

// Emitted when a sandbox widget (JSX, Desmos, plot, geometry) the student can
// see is still on its loading spinner after the render timeout. state_str is
// the leg of the connect/init/render handshake it stalled in.
export class SandboxWidgetStallEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Sandbox Widget Stall';
  constructor(opts: {
    state: string;
    pending: string;
    controlVisible: boolean;
    loads: number;
    iframeIntersecting: boolean | undefined;
    iframeRatio: number | undefined;
    rectWidth: number;
    rectHeight: number;
    bundleUrl: string;
    widgetType: string;
    controlKind: 'sandbox' | 'printer';
    trace: string;
    traceTruncated: boolean;
    notes: string;
    controlAgeMs: number;
    waitMs: number;
    reportOrdinal: number;
    reportCap: number;
    documentVisibility: string;
    hiddenDuringWait: boolean;
    userAgent: string;
    release: string | undefined;
  }) {
    super(SandboxWidgetStallEvent.EVENT_NAME, {
      'state_str': opts.state,
      'pending_str': opts.pending,
      'control_visible_bool': opts.controlVisible,
      'loads_int': opts.loads,
      'iframe_intersecting_str': String(opts.iframeIntersecting ?? 'never'),
      'iframe_ratio_str': String(opts.iframeRatio ?? 'never'),
      'rect_width_int': opts.rectWidth,
      'rect_height_int': opts.rectHeight,
      'bundle_url_str': opts.bundleUrl,
      'widget_type_str': opts.widgetType,
      'control_kind_str': opts.controlKind,
      'trace_str': opts.trace,
      'trace_truncated_bool': opts.traceTruncated,
      'notes_str': opts.notes,
      'control_age_ms_int': Math.round(opts.controlAgeMs),
      'wait_ms_int': Math.round(opts.waitMs),
      'report_ordinal_int': opts.reportOrdinal,
      'report_cap_int': opts.reportCap,
      'document_visibility_str': opts.documentVisibility,
      'hidden_during_wait_bool': opts.hiddenDuringWait,
      'user_agent_str': opts.userAgent,
      'release_str': opts.release ?? 'unknown',
    });
  }
}

export class ApplicationLaunchedEvent extends AnalyticsCustomEvent {
  private static readonly EVENT_NAME = 'Application Launched';

  constructor(
    application: 'student' | 'teacher',
    courseRef?: string,
    assignmentRef?: string
  ) {
    super(ApplicationLaunchedEvent.EVENT_NAME, {
      'assignment_str': assignmentRef || 'none',
      'course_str': courseRef || 'none',
      'application_str': application,
    });
  }
}
